'use client'

import { Skeleton } from "@/components/ui/skeleton"
import { useTurnos } from "@/hooks/use-turnos"
import { useRouter } from "next/navigation"
import type { TurnoOut } from "@/types/api"
import { cn } from "@/lib/utils"
import { format } from "date-fns"

const ESTADO_CLASSES: Record<string, string> = {
  pendiente:   "bg-amber-100 text-amber-700 border-amber-200",
  confirmado:  "bg-blue-100 text-blue-700 border-blue-200",
  completado:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  no_asistio:  "bg-rose-100 text-rose-500 border-rose-200",
  cancelado:   "bg-rose-100 text-rose-700 border-rose-200",
}

const STATUS_LABEL: Record<string, string> = {
  pendiente:  'Pendiente',
  confirmado: 'Confirmado',
  completado: 'Completado',
  no_asistio: 'No asistió',
  cancelado:  'Cancelado',
}

function formatFechaHora(iso: string) {
  try {
    return format(new Date(iso), "dd/MM/yy HH:mm")
  } catch {
    return iso
  }
}

export function AgendaList() {
  const today = new Date().toISOString().split('T')[0]
  const { data: response, isLoading } = useTurnos({ desde: today, limit: 8 })
  const router = useRouter()

  if (isLoading) {
    return (
      <div className="border border-border rounded-xl overflow-hidden divide-y divide-border bg-card">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex items-center gap-4 px-5 py-4">
            <Skeleton className="h-4 w-12 shrink-0 opacity-40" />
            <Skeleton className="h-4 w-40 opacity-40" />
            <Skeleton className="h-4 w-24 ml-auto opacity-40" />
          </div>
        ))}
      </div>
    )
  }

  const turnosHoy = response?.items ?? []

  if (turnosHoy.length === 0) {
    return (
      <div className="border border-border rounded-xl p-12 text-center bg-card border-dashed">
        <div className="flex flex-col items-center gap-3">
          <span className="text-3xl grayscale opacity-50">📅</span>
          <p className="text-[13px] font-medium text-muted-foreground tracking-tight">Sin turnos para hoy</p>
        </div>
      </div>
    )
  }

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-card shadow-sm">

      {/* Vista desktop — tabla con columnas */}
      <div className="hidden sm:block">
        <div className="grid grid-cols-[105px_1.5fr_1.5fr_70px_110px_90px] gap-4 px-5 py-3 bg-muted border-b border-border">
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Fecha/Hora</span>
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Paciente</span>
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Servicio</span>
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Duración</span>
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Profesional</span>
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest text-right">Estado</span>
        </div>
        <div className="divide-y divide-border">
          {turnosHoy.map((turno: TurnoOut) => (
            <button
              key={turno.id}
              onClick={() => router.push(`/pacientes/${turno.paciente_dni}`)}
              className="w-full grid grid-cols-[105px_1.5fr_1.5fr_70px_110px_90px] gap-4 px-5 py-3.5 text-left hover:bg-muted/50 transition-all duration-200 group"
            >
              <span className="text-[12px] tabular text-muted-foreground font-bold group-hover:text-foreground transition-colors self-center">
                {formatFechaHora(turno.fecha_hora)}
              </span>
              <div className="flex flex-col overflow-hidden self-center">
                <span className="text-[13px] font-semibold text-foreground truncate group-hover:translate-x-0.5 transition-transform">{turno.paciente_nombre}</span>
                <span className="text-[10px] text-muted-foreground font-mono truncate">{turno.paciente_dni}</span>
              </div>
              <span className="text-[12px] text-muted-foreground/80 truncate font-medium self-center">{turno.procedimiento_nombre}</span>
              <span className="text-[12px] text-muted-foreground/80 truncate font-medium self-center">{turno.duracion_min}min</span>
              <span className="text-[12px] text-muted-foreground/80 truncate font-medium self-center">{turno.profesional_nombre}</span>
              <div className="flex items-center justify-end self-center">
                <span className={cn(
                  "px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border shadow-sm",
                  ESTADO_CLASSES[turno.estado]
                )}>
                  {STATUS_LABEL[turno.estado]}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Vista móvil — cards */}
      <div className="sm:hidden divide-y divide-border">
        {turnosHoy.map((turno: TurnoOut) => (
          <button
            key={turno.id}
            onClick={() => router.push(`/pacientes/${turno.paciente_dni}`)}
            className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors"
          >
            {/* Fila superior: fecha + estado */}
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-[11px] font-mono text-muted-foreground font-bold">
                {formatFechaHora(turno.fecha_hora)}
              </span>
              <span className={cn(
                "px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border",
                ESTADO_CLASSES[turno.estado]
              )}>
                {STATUS_LABEL[turno.estado]}
              </span>
            </div>
            {/* Fila media: paciente */}
            <p className="text-[13px] font-semibold text-foreground truncate">{turno.paciente_nombre}</p>
            <p className="text-[10px] text-muted-foreground font-mono mb-1.5">{turno.paciente_dni}</p>
            {/* Fila inferior: procedimiento + profesional + duración */}
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
              <span className="truncate">{turno.procedimiento_nombre}</span>
              <span>·</span>
              <span className="truncate">{turno.profesional_nombre}</span>
              <span>·</span>
              <span>{turno.duracion_min}min</span>
            </div>
          </button>
        ))}
      </div>

    </div>
  )
}
